/**
 * OutreachEmailModal - AI-powered cold outreach email generator
 *
 * Queues a text generation job and listens for the result via
 * account-level SSE. The generated email is persisted on the lead
 * so it survives page reloads.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { SpinnerIcon } from "@/components/icons";
import { leadService } from "../Lead.service";
import { copyToClipboard } from "@/utils/clipboard";
import { useAccountRealtimeSync } from "@/hooks/useAccountRealtimeSync";
import type { Lead } from "../Lead.types";

type Stage = "input" | "generating" | "result";

interface OutreachEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  lead: Lead | null;
  onEmailGenerated?: (leadId: string) => void;
}

interface GeneratedEmail {
  email: string;
  language: string;
  leadName: string;
}

export function OutreachEmailModal({
  isOpen,
  onClose,
  lead,
  onEmailGenerated,
}: OutreachEmailModalProps) {
  const [stage, setStage] = useState<Stage>("input");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [generatedEmail, setGeneratedEmail] = useState<GeneratedEmail | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [canClose, setCanClose] = useState(false);
  const [progressMessage, setProgressMessage] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  // Track which lead we're generating for so SSE events match
  const generatingLeadIdRef = useRef<string | null>(null);
  const activeJobIdRef = useRef<string | null>(null);

  const { on: onAccountEvent } = useAccountRealtimeSync();

  // Listen for outreach_email_generated SSE events
  useEffect(() => {
    const unsubscribe = onAccountEvent(
      "outreach_email_generated",
      (data: Record<string, unknown>) => {
        const leadId = data.leadId as string;
        if (leadId !== generatingLeadIdRef.current) return;

        setGeneratedEmail({
          email: data.email as string,
          language: data.language as string,
          leadName: data.leadName as string,
        });
        setStage("result");
        setProgressMessage(null);
        setProgress(0);
        generatingLeadIdRef.current = null;
        activeJobIdRef.current = null;
        onEmailGenerated?.(leadId);
      }
    );
    return unsubscribe;
  }, [onAccountEvent, onEmailGenerated]);

  // Listen for job progress events on the account channel
  useEffect(() => {
    const unsubProgress = onAccountEvent(
      "job_progress",
      (data: Record<string, unknown>) => {
        const jobId = data.id as string;
        if (jobId !== activeJobIdRef.current) return;
        if (data.progressMessage) {
          setProgressMessage(data.progressMessage as string);
        }
        if (typeof data.progress === "number") {
          setProgress(data.progress as number);
        }
      }
    );

    const unsubFailed = onAccountEvent(
      "job_completed",
      (data: Record<string, unknown>) => {
        const jobId = data.id as string;
        if (jobId !== activeJobIdRef.current) return;
        if (data.status === "failed") {
          setError(
            (data.errorMessage as string) || "Email generation failed"
          );
          setStage("input");
          generatingLeadIdRef.current = null;
          activeJobIdRef.current = null;
        }
      }
    );

    return () => {
      unsubProgress();
      unsubFailed();
    };
  }, [onAccountEvent]);

  // Reset state when lead changes or modal opens with a new lead
  useEffect(() => {
    if (!isOpen || !lead) return;

    // If we have a cached result for this lead, show it
    if (generatedEmail && generatedEmail.leadName === lead.name) {
      setStage("result");
      return;
    }

    // If we're already generating for this lead, show generating
    if (generatingLeadIdRef.current === lead.id) {
      setStage("generating");
      setCanClose(true);
      return;
    }

    // Load persisted email from lead data
    if (lead.outreachEmailData) {
      setGeneratedEmail({
        email: lead.outreachEmailData.email,
        language: lead.outreachEmailData.language,
        leadName: lead.name,
      });
      setStage("result");
      return;
    }

    // Fresh open
    setStage("input");
    setError(null);
    setCopied(false);
    setProgressMessage(null);
    setProgress(0);
  }, [isOpen, lead, generatedEmail]);

  const handleGenerate = useCallback(async () => {
    if (!lead) return;

    setStage("generating");
    setError(null);
    setCanClose(false);
    setProgressMessage(null);
    setProgress(0);
    generatingLeadIdRef.current = lead.id;

    // Allow dismissal after 1 second
    setTimeout(() => setCanClose(true), 1000);

    try {
      const result = await leadService.generateOutreachEmail(
        lead.id,
        additionalNotes
      );
      activeJobIdRef.current = result.jobId;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to start generation"
      );
      setStage("input");
      generatingLeadIdRef.current = null;
    }
  }, [lead, additionalNotes]);

  const handleCopy = useCallback(async () => {
    if (!generatedEmail) return;
    const success = await copyToClipboard(generatedEmail.email);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [generatedEmail]);

  const handleRegenerate = useCallback(() => {
    setGeneratedEmail(null);
    setStage("input");
    setCopied(false);
    setProgressMessage(null);
    setProgress(0);
  }, []);

  const handleClose = () => {
    if (stage === "generating" && !canClose) return;
    onClose();
  };

  if (!lead) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Outreach Email - ${lead.name}`}
      size="lg"
    >
      <div className="p-6">
        {stage === "input" && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-3">
                Generate a personalized cold email for{" "}
                <span className="font-medium text-gray-900">{lead.name}</span>
                {lead.city && ` in ${lead.city}`}. The AI will reference their
                online presence and write in the local language.
              </p>
            </div>

            <div>
              <label
                htmlFor="outreach-notes"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Additional notes (optional)
              </label>
              <textarea
                id="outreach-notes"
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                placeholder="e.g. We specialize in social media for boutique hotels, aesthetic clinics, wellness studios and other appointment-led businesses; mention recent work with similar clients..."
                className="w-full h-28 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
              />
            </div>

            {/* Lead context preview */}
            <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 space-y-1">
              <p className="font-medium text-gray-700 mb-1">
                Context the AI will use:
              </p>
              <p>
                Sector: {lead.sector || "Unknown"} | Location:{" "}
                {lead.city || "Unknown"}, {lead.country || "Unknown"}
              </p>
              {lead.instagramFollowers && (
                <p>
                  Instagram: {lead.instagramFollowers.toLocaleString()} followers
                </p>
              )}
              {lead.googleRating && (
                <p>
                  Google: {lead.googleRating}/5 ({lead.googleReviews} reviews)
                </p>
              )}
              {lead.website && <p>Website: {lead.website}</p>}
            </div>

            <div className="flex justify-end">
              <Button onClick={handleGenerate} variant="primary">
                Generate Email
              </Button>
            </div>
          </div>
        )}

        {stage === "generating" && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <SpinnerIcon className="w-12 h-12 text-purple-600" />
            <p className="text-gray-600 font-medium">
              {progressMessage || "Crafting outreach email..."}
            </p>
            {progress > 0 && (
              <div className="w-64 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
            <p className="text-sm text-gray-500">This may take a few moments</p>
            {canClose && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg max-w-md">
                <p className="text-sm text-blue-800 text-center">
                  You can close this modal while the email is being generated.
                  <br />
                  Re-open it to see the result when ready.
                </p>
              </div>
            )}
          </div>
        )}

        {stage === "result" && generatedEmail && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Language: {generatedEmail.language}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleRegenerate}>
                  Regenerate
                </Button>
                <Button
                  variant={copied ? "ghost" : "primary"}
                  size="sm"
                  onClick={handleCopy}
                >
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans leading-relaxed">
                {generatedEmail.email}
              </pre>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
